import { CloseCircleFilled } from "@ant-design/icons";
import { Empty, Input, Select } from "antd";
import useFeedbacks from "hooks/feedbacks/useFeedbacks";
import useDebounce from "hooks/route-rules/useDebounce";
import React, { useEffect, useState } from "react";
import { Spinner, Col, Row } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import FeedbacksTable from "./FeedbacksTable";

export default function Feedbacks() {
  const {
    feedbacks,
    loading,
    totalPages,
    totalElements,
    fetchAllFeedbacks,
    fetchFeedbackById,
    deleteFeedbackById,
    searchFeedbacks,
  } = useFeedbacks();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFeeling, setSelectedFeeling] = useState("ALL");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchRequest = debouncedSearchTerm.trim().length >= 3;

  const subjectOptions = [
    { label: "\u{1F41E} APP_BUG", value: "APP_BUG" },
    { label: "\u26A1 PERFORMANCE", value: "PERFORMANCE" },
    { label: "\u2728 FEATURE_REQUEST", value: "FEATURE_REQUEST" },
    { label: "\u{1F3A8} UI_FEEDBACK", value: "UI_FEEDBACK" },
    { label: "\u{1F4B3} PAYMENTS", value: "PAYMENTS" },
    { label: "\u2705 TASKS", value: "TASKS" },
    { label: "\u{1F6E1}\uFE0F SAFETY", value: "SAFETY" },
    { label: "\u{1F6AB} HARASSMENT", value: "HARASSMENT" },
    { label: "\u{1F464} ACCOUNT", value: "ACCOUNT" },
    { label: "\u{1FAAA} VERIFICATION", value: "VERIFICATION" },
    { label: "\u{1F6E0}\uFE0F SUPPORT", value: "SUPPORT" },
    { label: "\u{1F4AC} GENERAL_FEEDBACK", value: "GENERAL_FEEDBACK" },
    { label: "\u{1F4CC} OTHER", value: "OTHER" },
  ];

  useEffect(() => {
    const trimmedSearch = debouncedSearchTerm.trim();
    const hasFeelingFilter = selectedFeeling !== "ALL";
    const hasSubjectFilter = selectedSubject.trim().length > 0;

    if (trimmedSearch.length === 0 && !hasFeelingFilter && !hasSubjectFilter) {
      fetchAllFeedbacks("", page, pageSize);
      return;
    }

    if (
      trimmedSearch.length > 0 &&
      trimmedSearch.length < 3 &&
      !hasFeelingFilter &&
      !hasSubjectFilter
    ) {
      // Match health checks behavior: 1-2 chars keeps default paged list.
      fetchAllFeedbacks("", page, pageSize);
      return;
    }

    searchFeedbacks(
      trimmedSearch,
      "ALL",
      selectedFeeling,
      selectedSubject,
      page,
      pageSize
    );
  }, [
    debouncedSearchTerm,
    fetchAllFeedbacks,
    page,
    pageSize,
    searchFeedbacks,
    selectedFeeling,
    selectedSubject,
  ]);

  return (
    <>
      <WidgetSectionTitle
        icon="comment"
        title="Feedbacks"
        subtitle="View, track, and manage all user feedback efficiently in one place."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <Row
        className="mb-3 align-items-end gx-3 p-2 rounded bg-light mx-auto"
        style={{ maxWidth: 980 }}
      >
        <Col md={6}>
          <div className="text-muted small fw-semibold mb-1">
            {"\u{1F50E} Search Content"}
          </div>
          <Input
            placeholder='Search by "userId" or "content" (min. 3 chars)...'
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            allowClear={{
              clearIcon: (
                <CloseCircleFilled
                  onClick={() => setSearchTerm("")}
                  style={{ color: "#8c8c8c" }}
                />
              ),
            }}
            style={{
              width: "63%",
              borderRadius: 24,
              borderColor:
                searchTerm.length > 0 && searchTerm.length < 3 ? "#ffa940" : "",
            }}
            suffix={
              loading && isSearchRequest ? (
                <Spinner animation="border" size="sm" variant="success" />
              ) : (
                searchTerm.length > 0 &&
                searchTerm.length < 3 && (
                  <small className="text-warning" style={{ fontSize: "10px" }}>
                    Type {3 - searchTerm.length} more...
                  </small>
                )
              )
            }
          />
        </Col>
        <Col md={3} className="pe-md-3">
          <div className="text-muted small fw-semibold mb-1">
            {"\u{1F60A} Feeling Filter"}
          </div>
          <Select
            style={{ width: "100%" }}
            value={selectedFeeling}
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            styles={{ popup: { root: { minWidth: 240 } } }}
            placeholder="Select feeling"
            onChange={(value) => {
              setSelectedFeeling(value);
              setPage(0);
            }}
            options={[
              { label: "All Feelings", value: "ALL" },
              { label: "\u{1F44D} LIKE", value: "LIKE" },
              { label: "\u{1F44E} DISLIKE", value: "DISLIKE" },
              { label: "\u{1F60A} HAPPY", value: "HAPPY" },
              { label: "\u{1F614} SAD", value: "SAD" },
            ]}
          />
        </Col>
        <Col md={3} className="ps-md-3">
          <div className="text-muted small fw-semibold mb-1">
            {"\u{1F3F7}\uFE0F Subject Filter"}
          </div>
          <Select
            style={{ width: "100%" }}
            value={selectedSubject || undefined}
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            styles={{ popup: { root: { minWidth: 340 } } }}
            placeholder="Filter by subject"
            allowClear
            onClear={() => {
              setSelectedSubject("");
              setPage(0);
            }}
            onChange={(value) => {
              setSelectedSubject(value || "");
              setPage(0);
            }}
            options={subjectOptions}
          />
        </Col>
      </Row>

      {loading && feedbacks.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner animation="border" variant="success" />
        </div>
      ) : feedbacks && feedbacks.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty
            description={
              searchTerm.trim() && selectedFeeling !== "ALL" && selectedSubject.trim()
                ? `No feedbacks found for "${searchTerm.trim()}" with feeling "${selectedFeeling}" and subject "${selectedSubject.trim()}".`
                : searchTerm.trim() && selectedFeeling !== "ALL"
                ? `No feedbacks found for "${searchTerm.trim()}" with feeling "${selectedFeeling}".`
                : searchTerm.trim() && selectedSubject.trim()
                ? `No feedbacks found for "${searchTerm.trim()}" with subject "${selectedSubject.trim()}".`
                : searchTerm.trim()
                ? `No feedbacks found for "${searchTerm.trim()}".`
                : selectedFeeling !== "ALL" && selectedSubject.trim()
                ? `No feedbacks found with feeling "${selectedFeeling}" and subject "${selectedSubject.trim()}".`
                : selectedFeeling !== "ALL"
                ? `No feedbacks found with feeling "${selectedFeeling}".`
                : selectedSubject.trim()
                ? `No feedbacks found with subject "${selectedSubject.trim()}".`
                : "No feedbacks have been submitted yet"
            }
          />
        </div>
      ) : (
        <FeedbacksTable
          feedbacks={feedbacks}
          getFeedback={fetchFeedbackById}
          deleteFeedback={deleteFeedbackById}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          totalPages={totalPages}
          totalElements={totalElements}
        />
      )}
    </>
  );
}
